import { AUTO_SUB_MAX_COST, assignSlots, improvisationCost, type Position } from "@legacy-manager/engine";

/**
 * Quem serve para cada vaga, do jeito que o motor pensa.
 *
 * A troca de formação na Tática e no intervalo, e as sugestões que pulsam
 * quando o treinador pega uma vaga, usam a mesma conta do motor: quem é da
 * posição vem antes, a vizinha só na falta dele, e o outro lado do campo
 * (atacante na zaga) nunca por escolha do jogo. Improvisar continua livre,
 * à mão.
 */

/** O custo de pôr alguém da posição `natural` na vaga, com as siglas da view. */
export function custoDeImproviso(natural: string, vaga: string): number {
  return improvisationCost(natural as Position, vaga as Position);
}

/**
 * Os mesmos candidatos distribuídos pelas vagas, um por vaga, na ordem delas.
 * Nulo onde faltou gente.
 */
export function distribuir<T>(
  vagas: readonly string[],
  candidatos: readonly T[],
  posicaoDe: (candidato: T) => string,
  forcaNaVaga: (candidato: T, vaga: string) => number,
): Array<T | null> {
  return assignSlots(vagas as Position[], candidatos, (c) => posicaoDe(c) as Position, forcaNaVaga);
}

/**
 * Os candidatos que a tela sugere para a vaga: os da posição e, se não houver
 * nenhum, os da posição vizinha. Vazio quando só sobra improviso de verdade.
 */
export function sugeridosPara<T>(vaga: string, candidatos: readonly T[], posicaoDe: (candidato: T) => string): T[] {
  let menor = Infinity;
  for (const candidato of candidatos) menor = Math.min(menor, custoDeImproviso(posicaoDe(candidato), vaga));
  if (menor > AUTO_SUB_MAX_COST) return [];
  return candidatos.filter((candidato) => custoDeImproviso(posicaoDe(candidato), vaga) === menor);
}

/** O caminho inverso: as vagas que a tela sugere para um jogador, pelos índices. */
export function vagasSugeridas(natural: string, vagas: readonly string[]): number[] {
  const custos = vagas.map((vaga) => custoDeImproviso(natural, vaga));
  const menor = Math.min(...custos);
  if (menor > AUTO_SUB_MAX_COST) return [];
  return custos.flatMap((custo, indice) => (custo === menor ? [indice] : []));
}
